/**
 * Bid Requests Page
 * BuilderBel Construction Project Management System
 */

import {
  Award,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileText,
  Pencil,
  Plus,
  Send,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, SearchableDropdown, TextArea, Dropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { BidRequestDrawer } from '../components/bidRequests';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useAwardBidRequestMutation,
  useCloseBidRequestMutation,
  useDeleteBidRequestMutation,
  useGetBidRequestStatsQuery,
  useGetBidRequestsQuery,
  useIssueBidRequestMutation,
} from '../services/bidRequestApi';
import type { BidRequest, BidRequestStatus } from '../types/bidRequest';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'closed', label: 'Closed' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'name', label: 'Name' },
  { value: 'trade', label: 'Trade' },
  { value: 'status', label: 'Status' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'bidNumber', label: 'Bid Number' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: BidRequestStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' => {
  switch (status) {
    case 'awarded':
      return 'success';
    case 'issued':
      return 'info';
    case 'closed':
      return 'purple';
    case 'cancelled':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

// ============================================================================
// COMPONENT
// ============================================================================

const BidRequests: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<BidRequestStatus | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBidRequest, setSelectedBidRequest] = useState<BidRequest | null>(null);

  // Award modal
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [awardingBidRequest, setAwardingBidRequest] = useState<BidRequest | null>(null);
  const [awardResponseId, setAwardResponseId] = useState('');
  const [awardNotes, setAwardNotes] = useState('');

  // Queries
  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(status && { status }),
      ...(search && { search }),
      sortBy,
      sortOrder,
    }),
    [page, status, search, sortBy, sortOrder]
  );

  const { data: listData, isLoading } = useGetBidRequestsQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetBidRequestStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  // Mutations
  const [deleteBidRequest] = useDeleteBidRequestMutation();
  const [issueBidRequest] = useIssueBidRequestMutation();
  const [closeBidRequest] = useCloseBidRequestMutation();
  const [awardBidRequest] = useAwardBidRequestMutation();

  const bidRequests = listData?.data?.bidRequests || [];
  const pagination = listData?.data?.pagination;
  const stats = statsData?.data;

  // ---- Handlers ----

  const handleCreate = () => {
    setSelectedBidRequest(null);
    setDrawerOpen(true);
  };

  const handleEdit = useCallback((bid: BidRequest) => {
    setSelectedBidRequest(bid);
    setDrawerOpen(true);
  }, []);

  const handleView = useCallback((bid: BidRequest) => {
    setSelectedBidRequest(bid);
    setDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (bid: BidRequest) => {
      if (!projectId) return;
      if (
        !window.confirm(`Delete bid request "${bid.name}"? This cannot be undone.`)
      )
        return;
      try {
        await deleteBidRequest({ projectId, bidRequestId: bid._id }).unwrap();
        toast.success('Bid request deleted');
      } catch {
        toast.error('Failed to delete bid request');
      }
    },
    [projectId, deleteBidRequest, toast]
  );

  const handleIssue = useCallback(
    async (bid: BidRequest) => {
      if (!projectId) return;
      try {
        await issueBidRequest({ projectId, bidRequestId: bid._id }).unwrap();
        toast.success('Bid request issued to vendors');
      } catch {
        toast.error('Failed to issue bid request');
      }
    },
    [projectId, issueBidRequest, toast]
  );

  const handleClose = useCallback(
    async (bid: BidRequest) => {
      if (!projectId) return;
      try {
        await closeBidRequest({ projectId, bidRequestId: bid._id }).unwrap();
        toast.success('Bid request closed');
      } catch {
        toast.error('Failed to close bid request');
      }
    },
    [projectId, closeBidRequest, toast]
  );

  const handleOpenAwardModal = useCallback((bid: BidRequest) => {
    setAwardingBidRequest(bid);
    setAwardResponseId('');
    setAwardNotes('');
    setAwardModalOpen(true);
  }, []);

  const handleAward = useCallback(async () => {
    if (!projectId || !awardingBidRequest || !awardResponseId.trim()) return;
    try {
      await awardBidRequest({
        projectId,
        bidRequestId: awardingBidRequest._id,
        data: { bidResponseId: awardResponseId.trim(), notes: awardNotes.trim() },
      }).unwrap();
      toast.success('Bid request awarded');
      setAwardModalOpen(false);
      setAwardingBidRequest(null);
    } catch {
      toast.error('Failed to award bid request');
    }
  }, [projectId, awardingBidRequest, awardResponseId, awardNotes, awardBidRequest, toast]);

  const handleDownloadPdf = useCallback(
    async (bid: BidRequest) => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/bid-requests/${bid._id}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('PDF generation failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `bid-request-${bid.bidNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Build action menu items for each row
  const getActions = useCallback(
    (bid: BidRequest): (ActionMenuItem | 'divider')[] => {
      const isDraft = bid.status === 'draft';
      const isIssued = bid.status === 'issued';
      const isClosed = bid.status === 'closed';

      const items: (ActionMenuItem | 'divider')[] = [
        {
          label: 'View Details',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleView(bid),
        },
      ];

      if (isDraft) {
        items.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleEdit(bid),
        });
        items.push({
          label: 'Issue to Vendors',
          icon: <Send className="h-4 w-4" />,
          onClick: () => handleIssue(bid),
        });
      }

      if (isIssued) {
        items.push({
          label: 'Close Bidding',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleClose(bid),
        });
      }

      if (isClosed) {
        items.push({
          label: 'Award Bid',
          icon: <Award className="h-4 w-4" />,
          onClick: () => handleOpenAwardModal(bid),
          variant: 'success',
        });
      }

      items.push('divider');
      items.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPdf(bid),
      });

      if (isDraft) {
        items.push('divider');
        items.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(bid),
          variant: 'danger',
        });
      }

      return items;
    },
    [
      handleView,
      handleEdit,
      handleIssue,
      handleClose,
      handleOpenAwardModal,
      handleDownloadPdf,
      handleDelete,
    ]
  );

  // ---- Stats tiles ----

  const statsTiles = useMemo(
    () => [
      {
        title: 'Total',
        value: stats?.total || 0,
        icon: <FileText className="h-4 w-4 text-brand-600" />,
      },
      {
        title: 'Issued',
        value: stats?.issued || 0,
        icon: <Send className="h-4 w-4 text-blue-500" />,
      },
      {
        title: 'Responses',
        value: stats?.totalResponses || 0,
        icon: <Users className="h-4 w-4 text-purple-500" />,
      },
      {
        title: 'Awarded',
        value: stats?.awarded || 0,
        icon: <Award className="h-4 w-4 text-green-600" />,
      },
    ],
    [stats]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!projectId) {
    return (
      <ModuleLayout
        title="Bid Requests"
        description="Solicit competitive bids from subcontractors and suppliers"
        icon={<FileText className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={FileText}
          title="No project selected"
          description="Select a project to view bid requests."
        />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      title="Bid Requests"
      description="Create bid packages, invite vendors, and compare bids"
      icon={<FileText className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Create Bid Request',
        onClick: handleCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={statsTiles}
    >
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px] max-w-xs">
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search bid requests..."
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_OPTIONS}
            value={status}
            onChange={val => {
              setStatus(val as BidRequestStatus | '');
              setPage(1);
            }}
            placeholder="Status"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => setSortBy(val)}
            placeholder="Sort by"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      ) : bidRequests.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No bid requests found"
          description={
            search || status
              ? 'No bid requests match your current filters. Try adjusting them.'
              : 'Create your first bid request to start soliciting competitive bids from vendors.'
          }
          action={
            !search && !status
              ? { label: 'Create Bid Request', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Bid Request
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Trade
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Due Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Vendors
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Responses
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Budget Ref.
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Status
                  </th>
                  <th className="w-10 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bidRequests.map(bid => (
                  <tr
                    key={bid._id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleView(bid)}
                  >
                    {/* Bid number + name */}
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 leading-tight">
                            {bid.name}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">{bid.bidNumber}</p>
                        </div>
                      </div>
                    </td>

                    {/* Trade */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{bid.trade}</span>
                    </td>

                    {/* Due Date */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {bid.dueDate ? (
                          <>
                            <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600">
                              {formatDate(bid.dueDate)}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </td>

                    {/* Vendors Invited */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {bid.invitations?.length || 0}
                      </span>
                    </td>

                    {/* Responses */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {bid.responses?.length || 0}
                      </span>
                    </td>

                    {/* Budget Reference */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {bid.budgetAmount !== undefined
                          ? formatCurrency(bid.budgetAmount)
                          : '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={
                          bid.status.charAt(0).toUpperCase() + bid.status.slice(1)
                        }
                        variant={getStatusVariant(bid.status)}
                      />
                    </td>

                    {/* Actions */}
                    <td
                      className="px-4 py-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <ActionMenu id={bid._id} items={getActions(bid)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} bid requests
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Drawer */}
      <BidRequestDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBidRequest(null);
        }}
        projectId={projectId || ''}
        bidRequest={selectedBidRequest}
      />

      {/* Award Modal */}
      {awardModalOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" />
            <div className="fixed inset-0 flex items-center justify-center z-[120] p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Award Bid</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Select the winning bid response and confirm the award.
                  </p>
                </div>
                <div className="px-6 py-5 space-y-4">
                  {awardingBidRequest && awardingBidRequest.responses.length > 0 ? (
                    <div>
                      <label className="block text-xs text-gray-500 mb-2">
                        Select Winning Bid <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        options={awardingBidRequest.responses.map(response => ({
                          value: response._id || '',
                          label: `${response.vendorName} — ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: response.currency || 'USD',
                          }).format(response.totalAmount)}`,
                        }))}
                        value={awardResponseId}
                        onChange={val => setAwardResponseId(val)}
                        placeholder="Select winning bid response"
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No bid responses have been submitted yet.
                    </p>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">
                      Award Notes
                    </label>
                    <TextArea
                      value={awardNotes}
                      onChange={setAwardNotes}
                      rows={3}
                      placeholder="Reason for selection, next steps..."
                    />
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
                  <button
                    onClick={() => {
                      setAwardModalOpen(false);
                      setAwardingBidRequest(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAward}
                    disabled={
                      !awardResponseId.trim() ||
                      !awardingBidRequest ||
                      awardingBidRequest.responses.length === 0
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Award className="h-4 w-4" />
                    Award Bid
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Cancel icon for status badges (XCircle used for visual reference) */}
      <span className="hidden">
        <XCircle className="h-4 w-4" />
      </span>
    </ModuleLayout>
  );
};

export default BidRequests;
